import { useCallback, useEffect, useRef, useState } from 'react';
import { PrivateKey } from '@hashgraph/sdk';
import WalletConnector from '../connectors/walletconnect';
import { requestSession, signWithKey } from '../api/userAPI';
import { currentSession, isLapsed, SESSION_EXPIRED_EVENT, TOKEN_EVENT } from '../utils/session';
import { getWalletErrorMessage, isWalletRejection } from '../utils/walletErrors';
import { ModalType, NetworkType } from '../utils/constants';

interface SessionInputs {
  accountId: string;
  network: NetworkType | undefined;
  /** True once a wallet session is in place, restored after a reload or newly paired. */
  connected: boolean;
  /** The WalletConnect connector, once it has started and restored any wallet session. */
  connector: WalletConnector | undefined;
  /** Ends the connection but stays on this network. */
  signOut: () => void;
  triggerRefresh: () => void;
  openModal: (modal: ModalType, data?: Record<string, any>) => void;
  notify: (message: string, status: 'error' | 'info') => void;
}

/** Why a wallet sign-in failed, worded for the person, with what to do next. */
export const signInErrorMessage = (error: unknown): string =>
  /Request expired/i.test(String((error as any)?.message ?? error))
    ? "Your wallet didn't answer the sign-in request in time. Reload the page to try again"
    : getWalletErrorMessage(error, "Couldn't sign in. Reload the page to try again");

/**
 * The one owner of the backend session: it signs in, reuses a current
 * session, signs in again when the backend refuses one, and stores and
 * announces tokens.
 *
 * Each account, network and wallet connection is a generation. A sign-in
 * that finishes after its generation ended (the person switched account or
 * network, or disconnected) is ignored entirely: its token isn't stored, and
 * its failure neither shows a message nor signs anyone out.
 */
export function useSession({
  accountId,
  network,
  connected,
  connector,
  signOut,
  triggerRefresh,
  openModal,
  notify,
}: SessionInputs) {
  const generation = useRef(0);
  const generationKey = useRef('');
  /** The generation whose wallet sign-in is under way, so a second trigger doesn't prompt again. */
  const signingIn = useRef<number | null>(null);
  const announced = useRef<string | null>(null);
  /** The token the latest sign-in produced: if the backend refuses it right away, signing in again won't help. */
  const fresh = useRef<string | null>(null);
  const [refusals, setRefusals] = useState(0);
  const callbacks = useRef({ signOut, triggerRefresh, openModal, notify });
  callbacks.current = { signOut, triggerRefresh, openModal, notify };

  // Keyed by value, not by effect runs, so React's double effects in development don't end a generation
  const key = `${network}|${accountId}|${connected}`;
  useEffect(() => {
    if (generationKey.current !== key) {
      generationKey.current = key;
      generation.current += 1;
    }
  }, [key]);

  const announce = useCallback((token: string) => {
    if (announced.current === token) return;
    announced.current = token;
    window.dispatchEvent(new Event(TOKEN_EVENT));
  }, []);

  const adopt = useCallback(
    (token: string, sessionAccountId: string) => {
      fresh.current = token;
      localStorage.setItem('token', token);
      localStorage.setItem('accountId', sessionAccountId);
      announce(token);
      // Pages that loaded without a session fetch again
      callbacks.current.triggerRefresh();
    },
    [announce],
  );

  useEffect(() => {
    // Custom networks sign in through the keystore drawer (signInWithKey)
    if (!accountId || !network || network.startsWith('custom-')) return;
    const session = currentSession(accountId, network);
    if (session) {
      announce(session);
      return;
    }
    // WalletConnect is still starting, and may yet restore the wallet session
    if (!connector) return;
    const started = generation.current;
    if (signingIn.current === started) return;
    signingIn.current = started;
    if (!connected) {
      // Neither a session nor a wallet to sign a new one: don't sit there showing the account
      callbacks.current.notify(
        'Your wallet is no longer connected. Connect it again to continue',
        'info',
      );
      callbacks.current.signOut();
      return;
    }
    requestSession(accountId, network, async message => ({
      signatureMap: await connector.signLoginMessage(accountId, network, message),
    }))
      .then(token => {
        if (started === generation.current) adopt(token, accountId);
      })
      .catch(error => {
        if (started !== generation.current) return;
        console.error('Sign-in failed:', error);
        if (isWalletRejection(error)) {
          callbacks.current.notify(
            'Sign-in was declined in your wallet. Connect again to continue',
            'error',
          );
          callbacks.current.signOut();
        } else {
          // Keep the wallet paired: the person can try again without pairing anew
          callbacks.current.notify(signInErrorMessage(error), 'error');
        }
      })
      .finally(() => {
        if (signingIn.current === started) signingIn.current = null;
      });
  }, [accountId, network, connected, connector, refusals, adopt, announce]);

  useEffect(() => {
    const onRefused = (event: Event) => {
      const refused = (event as CustomEvent<{ token?: string }>).detail?.token;
      if (refused && refused === fresh.current && !isLapsed(refused)) {
        callbacks.current.notify(
          "The server didn't accept the new session. Reload the page to sign in again",
          'error',
        );
        return;
      }
      if (network?.startsWith('custom-')) {
        callbacks.current.openModal(ModalType.KeystoreAuthDrawer, { networkId: network });
        return;
      }
      setRefusals(count => count + 1);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onRefused);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onRefused);
  }, [network]);

  /**
   * Signs in on a custom network with a decrypted local key. Resolves true
   * once the session is stored, false if another sign-in or a disconnect
   * overtook it; rejects with the backend's reason.
   */
  const signInWithKey = useCallback(
    async (keyAccountId: string, networkId: string, privateKey: Pick<PrivateKey, 'sign'>) => {
      // This overtakes any sign-in under way, and the previous account's session ends now
      const started = ++generation.current;
      localStorage.removeItem('token');
      const token = await requestSession(keyAccountId, networkId, signWithKey(privateKey));
      if (started !== generation.current) return false;
      adopt(token, keyAccountId);
      return true;
    },
    [adopt],
  );

  /** Ends the generation, for a disconnect: whatever sign-in is under way is ignored. */
  const end = useCallback(() => {
    generation.current += 1;
  }, []);

  return { signInWithKey, end };
}
